import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { Droplets, Sprout, Flower2, CheckCircle, Circle, FileText, Download, ChevronDown, ChevronUp, Users, Share2, Church, Save, ExternalLink } from 'lucide-react';
import { AlphaVideoModal } from '../components/AlphaVideoModal';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DiscipleshipPath = () => {
  const { user, API } = useContext(AuthContext);
  const [updating, setUpdating] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // Collapsible states - separate state for each section
  const [openSections, setOpenSections] = useState({
    explore: true,
    engage: true,
    extend: true
  });
  
  // Fresh Expression names list
  const [freshExpressionNames, setFreshExpressionNames] = useState('');
  const [savingNames, setSavingNames] = useState(false);

  // Load saved names on mount
  useEffect(() => {
    const savedNames = localStorage.getItem(`freshExpression_${user?.id}`);
    if (savedNames) {
      setFreshExpressionNames(savedNames);
    }
  }, [user?.id]);

  const handleSaveNames = () => {
    setSavingNames(true);
    localStorage.setItem(`freshExpression_${user?.id}`, freshExpressionNames);
    setTimeout(() => {
      setSavingNames(false);
      toast.success('Names saved!');
    }, 500);
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const stages = [
    {
      id: 'explore',
      title: 'Explore',
      subtitle: 'Connect',
      icon: Droplets,
      color: '#2B6B7F',
      bgGradient: 'from-[#2B6B7F] to-[#3A8AA3]',
      description: 'Experience the Living Water to become alive',
      status: 'I sporadically connect somewhere in the Orchard of Jesus.',
      suggestions: [
        'Accept Jesus\' love for YOU',
        'Join an Alpha Group',
        'Ask the church office to connect you with a disciple making mentor',
        'More regularly attend "worship"',
        'Plug into any discipling group & be authentic about where you are in your belief in God',
        'Read the Bible'
      ],
      alphaLink: {
        text: '🔥 Watch Alpha Videos - Explore Christianity',
        url: '/resources?tab=additional#exploring-christianity'
      }
    },
    {
      id: 'engage',
      title: 'Engage',
      subtitle: 'Grow',
      icon: Sprout,
      color: '#5FA8C1',
      bgGradient: 'from-[#5FA8C1] to-[#7BA888]',
      description: 'Establish firm roots and branches',
      status: 'My "head" Vital Sign is healthy: I am baptized, in a discipling group, worshipping regularly, giving, walking in freedom, serving, & praying.',
      suggestions: [
        'Intentional yellow or pink Discipling groups',
        'Connect with a disciple making mentor',
        'Participate in a Wesley Group',
        'Attend different retreats offered for growth in specific areas',
        'Ask Jesus hard questions'
      ],
      resource: {
        title: 'Spiritual Gifts Guide',
        description: 'Discover and develop your spiritual gifts',
        url: 'https://customer-assets.emergentagent.com/job_pathway-wfmc/artifacts/hqf78yw8_Spiritual%20Gifts.pdf'
      }
    },
    {
      id: 'extend',
      title: 'Extend',
      subtitle: 'Neighbor',
      icon: Flower2,
      color: '#7BA888',
      bgGradient: 'from-[#7BA888] to-[#A4C9AD]',
      description: 'Bear fruit and spread seed',
      status: 'My "heart" and "hands" Vital Signs are regularly healthy: self-studying/praying, walking in gift(s) of the Spirit, evidence fruit of the Spirit, desire to be a disciple maker, and steward my whole life.',
      suggestions: [
        'Live as a Spirit-empowered missionary where you live, learn, work & play - listening and responding to the Spirit constantly',
        'Continue being made Holy',
        'Help others join and move on this journey: become a disciple making mentor (or as Jesus would call you - a disciple)',
        'Use the resources offered by the church to continue growing and discipling others'
      ],
      resource: {
        title: 'Discipleship Plan & Pathway Guide',
        description: 'Complete resource for making disciples who make disciples',
        url: '/discipleship-plan.pdf'
      },
      alphaLink: {
        text: '🏠 Lead an Alpha Group in Your Home - Access the Videos Here',
        url: '/resources?tab=additional#exploring-christianity'
      }
    }
  ];

  const handleStageUpdate = async (newStage) => {
    setUpdating(true);
    
    try {
      const response = await axios.put(`${API}/discipleship`, {
        current_stage: newStage
      });
      
      // Update local storage with new user data
      const updatedUser = { ...user, current_stage: newStage };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Discipleship stage updated!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setUpdating(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const currentStageIndex = stages.findIndex(s => s.id === user?.current_stage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="discipleship-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8 animate-fade-in text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F]">
              Discipleship Pathway
            </h1>
            <TutorialHint videoId="HEA-CVoURs4" />
          </div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your journey to joy-filled wholeness through three stages of spiritual growth
          </p>
        </div>

        {/* Share App Button */}
        <div className="flex justify-center mb-6">
          <Button
            onClick={() => setShowShareDialog(true)}
            variant="outline"
            className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/10"
            data-testid="share-app-button"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share This App
          </Button>
        </div>

        {/* Current Stage Highlight */}
        <Card className="mb-8 border-2 border-[#2B6B7F] shadow-lg" data-testid="current-stage-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2B6B7F] to-[#5FA8C1] flex items-center justify-center">
                {React.createElement(stages[currentStageIndex >= 0 ? currentStageIndex : 0]?.icon || Circle, {
                  className: 'w-8 h-8 text-white'
                })}
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-1">You are currently in:</p>
                <h2 className="text-2xl font-heading font-bold text-[#2B6B7F]">
                  {stages[currentStageIndex >= 0 ? currentStageIndex : 0]?.title} - {stages[currentStageIndex >= 0 ? currentStageIndex : 0]?.subtitle}
                </h2>
                <p className="text-slate-600 mt-1">
                  {stages[currentStageIndex >= 0 ? currentStageIndex : 0]?.description}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pathway Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              const isComplete = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              
              return (
                <React.Fragment key={stage.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                        isCurrent
                          ? `bg-gradient-to-br ${stage.bgGradient} shadow-lg scale-110`
                          : isComplete
                          ? 'bg-[#7BA888] shadow-md'
                          : 'bg-slate-200'
                      }`}
                      data-testid={`stage-indicator-${stage.id}`}
                    >
                      {isComplete ? (
                        <CheckCircle className="w-8 h-8 text-white" />
                      ) : (
                        <Icon className={`w-8 h-8 ${isCurrent ? 'text-white' : 'text-slate-400'}`} />
                      )}
                    </div>
                    <p className={`text-xs font-medium mt-2 ${
                      isCurrent ? 'text-[#2B6B7F]' : 'text-slate-600'
                    }`}>
                      {stage.title}
                    </p>
                  </div>
                  {index < stages.length - 1 && (
                    <div className="flex-1 h-1 mx-4 rounded-full" style={{
                      background: isComplete ? '#7BA888' : '#E2E8F0'
                    }}></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Collapsible Stage Details */}
        <div className="space-y-4">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isComplete = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const isOpen = openSections[stage.id];
            
            return (
              <Collapsible
                key={stage.id}
                open={isOpen}
                onOpenChange={() => toggleSection(stage.id)}
              >
                <Card
                  className={`overflow-hidden ${
                    isCurrent ? 'ring-2 ring-[#2B6B7F] shadow-lg' : ''
                  }`}
                  data-testid={`stage-card-${stage.id}`}
                >
                  <div
                    className={`h-2 bg-gradient-to-r ${stage.bgGradient}`}
                  ></div>
                  
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`collapsible-trigger-${stage.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${stage.bgGradient}`}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-xl font-heading flex items-center gap-2" style={{ color: stage.color }}>
                              {stage.title} - {stage.subtitle}
                              {isCurrent && (
                                <span className="text-xs bg-[#2B6B7F] text-white px-2 py-0.5 rounded-full">
                                  Current
                                </span>
                              )}
                              {isComplete && (
                                <CheckCircle className="w-5 h-5 text-[#7BA888]" />
                              )}
                            </CardTitle>
                            <CardDescription className="text-base">
                              {stage.description}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="p-2">
                          {isOpen ? (
                            <ChevronUp className="w-6 h-6 text-slate-500" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-slate-500" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="space-y-4 pt-0">
                      {/* Status */}
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Status:</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{stage.status}</p>
                      </div>
                      
                      {/* Suggestions */}
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">
                          Suggestions for {stage.subtitle === 'Connect' ? 'Connecting' : stage.subtitle === 'Grow' ? 'Growing' : 'Neighboring'}...
                        </p>
                        <ul className="space-y-2">
                          {stage.suggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: stage.color }}></div>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                        
                        {/* Alpha Link */}
                        {stage.alphaLink && (
                          <div className="mt-4 space-y-2">
                            <AlphaVideoModal
                              buttonLabel={stage.id === 'explore' ? 'Start Alpha at Episode 1' : 'Watch Alpha — Lead From Episode 1'}
                              fullWidth
                              testId={`start-alpha-${stage.id}`}
                            />
                            <a
                              href={stage.alphaLink.url}
                              className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                                stage.id === 'explore'
                                  ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-300 hover:border-red-400'
                                  : 'bg-gradient-to-r from-green-50 to-teal-50 border-green-300 hover:border-green-400'
                              }`}
                            >
                              <span className="text-sm font-medium" style={{ color: stage.id === 'explore' ? '#dc2626' : '#15803d' }}>
                                {stage.alphaLink.text}
                              </span>
                              <ExternalLink className="w-4 h-4" style={{ color: stage.id === 'explore' ? '#dc2626' : '#15803d' }} />
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Who is Jesus Video - Only for Explore stage */}
                      {stage.id === 'explore' && (
                        <div className="pt-4 mt-4 border-t border-slate-200">
                          <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200">
                            <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                              <span className="text-xl">🎬</span>
                              Who is Jesus and How Can We Trust the Bible?
                            </h5>
                            <p className="text-sm text-slate-600 mb-4">
                              Watch this helpful video exploring foundational questions about faith
                            </p>
                            <div className="aspect-video w-full rounded-lg overflow-hidden">
                              <iframe 
                                width="100%" 
                                height="100%" 
                                src="https://www.youtube.com/embed/HtTnSMNtE44?si=iZT9rLjLFbNamRxT&start=401" 
                                title="Who is Jesus and How Can We Trust the Bible?" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                referrerPolicy="strict-origin-when-cross-origin" 
                                allowFullScreen
                                className="w-full h-full"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Fresh Expression Names - Only for Extend stage */}
                      {stage.id === 'extend' && (
                        <div className="pt-4 mt-4 border-t border-slate-200">
                          <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-teal-50 border border-green-200">
                            <div className="flex items-center gap-2 mb-3">
                              <Church className="w-5 h-5 text-[#7BA888]" />
                              <h5 className="font-semibold text-slate-800">
                                Fresh Expression & Gospel Sharing
                              </h5>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">
                              Who is God stirring up in your heart to share the gospel with and/or start a Fresh Expression of church?
                            </p>
                            <Textarea
                              placeholder="Enter names of people God is placing on your heart..."
                              value={freshExpressionNames}
                              onChange={(e) => setFreshExpressionNames(e.target.value)}
                              className="min-h-[100px] mb-3"
                              data-testid="fresh-expression-textarea"
                            />
                            <Button
                              onClick={handleSaveNames}
                              disabled={savingNames}
                              size="sm"
                              className="bg-[#7BA888] hover:bg-[#5E8A6A]"
                              data-testid="save-names-button"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              {savingNames ? 'Saving...' : 'Save Names'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Ready to Lead Others? - Only for Extend stage */}
                      {stage.id === 'extend' && (
                        <div className="pt-4 mt-4 border-t border-slate-200">
                          <div className="p-4 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Church className="w-5 h-5 text-amber-600" />
                              <h5 className="font-semibold text-slate-800">
                                Ready to Lead Others?
                              </h5>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">
                              Learn how to start a Fresh Expression of church in your community
                            </p>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="border-amber-400 text-amber-700 hover:bg-amber-100 flex items-center gap-2"
                                  size="sm"
                                  data-testid="fresh-expression-guide-button"
                                >
                                  <Church className="w-4 h-4" />
                                  Resource for Beginning a Fresh Expression
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle className="text-2xl font-heading text-amber-700 flex items-center gap-2">
                                    <Church className="w-6 h-6" />
                                    You Can Lead a Fresh Expression of Church
                                  </DialogTitle>
                                  <DialogDescription className="text-slate-600">
                                    A simple guide to starting meaningful spiritual gatherings
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-6 mt-4">
                                  {/* Step 1 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200">
                                    <h3 className="font-bold text-lg text-rose-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-rose-600 text-white flex items-center justify-center text-sm font-bold">1</span>
                                      Connect People Over Food and/or Drinks and Intentional Conversation
                                    </h3>
                                    <p className="text-slate-700 mb-3">Use one of the following…</p>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span><strong>SOULARIUM cards</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span><strong>Deep Meaningful Conversations</strong> deck of cards</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span>Questions from <strong>Table Talk Card</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-rose-500">•</span>
                                        <span>Something else the Spirit may be drawing you to</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 2 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                                    <h3 className="font-bold text-lg text-blue-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">2</span>
                                      Center People Towards God
                                    </h3>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can listen to a worship song online</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can sing or play a song</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-blue-500">•</span>
                                        <span>You can read a Psalm</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 3 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                                    <h3 className="font-bold text-lg text-emerald-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">3</span>
                                      Have Jesus Conversations
                                    </h3>
                                    <p className="text-slate-700 mb-3">Use one of the following…</p>
                                    <ul className="space-y-2 text-slate-600">
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span><strong>Study a teaching of Jesus together</strong></span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span>Study passages on specific topics where you, as a group, can really understand God's heart around things like <em>faith, love, suffering, reasoning</em>, and so much more.</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <span className="text-emerald-500">•</span>
                                        <span>Share struggles and invite Jesus, the one who weeps with us, to be present in each life.</span>
                                      </li>
                                    </ul>
                                  </div>

                                  {/* Step 4 */}
                                  <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200">
                                    <h3 className="font-bold text-lg text-purple-700 mb-2 flex items-center gap-2">
                                      <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">4</span>
                                      Pray
                                    </h3>
                                    <p className="text-slate-600">
                                      Close your time together by lifting up prayers for one another, your community, and the world.
                                    </p>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      )}
                      
                      {!isCurrent && !isComplete && index === currentStageIndex + 1 && (
                        <Button
                          onClick={() => handleStageUpdate(stage.id)}
                          disabled={updating}
                          className="w-full mt-4"
                          style={{ backgroundColor: stage.color }}
                          data-testid={`advance-to-${stage.id}`}
                        >
                          {updating ? 'Updating...' : `Advance to ${stage.title}`}
                        </Button>
                      )}

                      {/* Resource Section */}
                      {stage.resource && (
                        <div className="pt-4 mt-4 border-t border-slate-200">
                          <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-200">
                            <FileText className="w-5 h-5 text-[#2B6B7F] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <h5 className="font-semibold text-sm text-slate-800 mb-1">
                                {stage.resource.title}
                              </h5>
                              <p className="text-xs text-slate-600 mb-3">
                                {stage.resource.description}
                              </p>
                              <div className="flex gap-2">
                                <a
                                  href={stage.resource.url}
                                  download
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#2B6B7F] hover:text-[#1E4D5F] border border-slate-300 rounded-md hover:bg-slate-50"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="mt-8 bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-heading text-lg font-semibold text-[#2B6B7F] mb-3">
              About the Discipleship Pathway
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              At Westland Free Methodist Church, we believe spiritual growth is a journey, not a destination. 
              The Discipleship Pathway guides you through three intentional stages: <strong>Explore</strong> (Connect with God), 
              <strong>Engage</strong> (Grow in faith), and <strong>Extend</strong> (Share with others). 
              Each stage builds on the previous one, helping you mature in your relationship with Christ and fulfill 
              your calling to make disciples who make disciples.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Share App Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
              <Share2 className="w-6 h-6" />
              Share This App
            </DialogTitle>
            <DialogDescription>
              Help others join the Discipleship Journey by sharing this app
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_c6c45f8d-756d-427c-8ad6-2091e406baff/artifacts/xh7hsqn2_NEW%20APP%20SITE%20%281200%20x%20500%20px%29.png"
              alt="App sharing instructions with QR code"
              className="w-full rounded-lg border border-slate-200"
              data-testid="share-qr-image"
            />
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 text-center">
                Scan the QR code or follow the instructions above to add this app to your phone's home screen for easy access.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={showPdfViewer} onOpenChange={setShowPdfViewer}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Discipleship Plan & Pathway Guide</DialogTitle>
            <DialogDescription>
              Complete resource for making disciples who make disciples
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-slate-100 rounded-lg p-4">
            <div className="flex flex-col items-center">
              <Document
                file="/discipleship-plan.pdf"
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="flex items-center justify-center p-8">
                    <div className="text-slate-600">Loading PDF...</div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center p-8">
                    <div className="text-red-600">Failed to load PDF. Please try downloading instead.</div>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-lg"
                  width={Math.min(window.innerWidth * 0.8, 800)}
                />
              </Document>
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-4">
            <Button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              variant="outline"
              size="sm"
            >
              Previous
            </Button>
            <p className="text-sm text-slate-600">
              Page {pageNumber} of {numPages || '...'}
            </p>
            <Button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
          <div className="flex justify-end gap-2 border-t pt-4">
            <a
              href="/discipleship-plan.pdf"
              download
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B6B7F] rounded-md hover:bg-[#1E4D5F]"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscipleshipPath;
